import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';

import type { SearchProductCardGlycerinTrackingItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingItemFragmentFragmentDoc';
import type { SearchProductCardGlycerinTrackingResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingResultFragmentFragmentDoc';
import type { SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc';
import type { WishlistButtonWithApiProps } from '@packages/modules/src/WishlistButtonWithApi/WishlistButtonWithApi';

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingResultFragment on SearchProductResult {
    categories {
      name
      isSelected
    }
  }
`;

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingItemFragment on StylePreviewItemBase {
    styleId
    styleName
    brand {
      name
    }
    listPosition
  }
`;

/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingVariationGroupFragment on CompleteVariationGroup {
    articleNumberWithPromoCode
    price {
      discount {
        previousPrice
      }
      currentPrice
      currency
      minInstallment
    }
  }
`;

export const buildGlycerinTrackingData = (
  maskedResult: FragmentType<typeof SearchProductCardGlycerinTrackingResultFragmentFragmentDoc>,
  maskedItem: FragmentType<typeof SearchProductCardGlycerinTrackingItemFragmentFragmentDoc>,
  maskedVariationGroup: FragmentType<
    typeof SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc
  >,
) => {
  const { styleId, listPosition } =
    unmask<typeof SearchProductCardGlycerinTrackingItemFragmentFragmentDoc>(maskedItem);
  const { articleNumberWithPromoCode, price } =
    unmask<typeof SearchProductCardGlycerinTrackingVariationGroupFragmentFragmentDoc>(
      maskedVariationGroup,
    );
  const { categories } =
    unmask<typeof SearchProductCardGlycerinTrackingResultFragmentFragmentDoc>(maskedResult);

  const currentCategoryName = categories.find((category) => category.isSelected)?.name;

  const listId = currentCategoryName || 'Search Result';

  const wishListTrackingProps: WishlistButtonWithApiProps['trackingProps'] = {
    products: [
      {
        productId: styleId,
        articleId: articleNumberWithPromoCode,
        // SKU is already automatically passed to the wishlist button for tracking from ProductCard, so no need to pass it here
        listPosition,
        origin: listId,
        currency: price.currency === 'EUR' || price.currency === 'CHF' ? price.currency : 'EUR',
        displayedPrice: price.currentPrice,
        originalPrice: price.discount?.previousPrice,
      },
    ],
  };

  return {
    wishListTrackingProps,
  };
};
